import React from 'react'
import { formatTakePrice, formatModifier } from '../../utils/formatters'

interface PriceWithModifierProps {
  value: string | number | null | undefined
  modifier: string | null | undefined
  className?: string
}

// Short modifiers that can stay on the same line as price
const SHORT_MODIFIERS = new Set(['+', '-', '+/-', '+-'])

/**
 * Renders a price with its modifier, with the modifier styled as bold italic
 * If a descriptor/modifier is provided but no price, still show the descriptor
 *
 * For long modifiers like "Motivated", "Firm", "Some Room", the modifier will
 * wrap to a second line when needed to prevent overflow into adjacent cells.
 */
export const PriceWithModifier: React.FC<PriceWithModifierProps> = ({
  value,
  modifier,
  className = ''
}) => {
  const formattedPrice = formatTakePrice(value)
  const displayModifier = formatModifier(modifier)

  // If no price but has a modifier/descriptor, show just the descriptor
  if (formattedPrice === '-' && displayModifier) {
    return <span className={`${className} font-bold italic`}>{displayModifier}</span>
  }

  // If no price and no modifier, show dash
  if (formattedPrice === '-' || !displayModifier) {
    return <span className={className}>{formattedPrice}</span>
  }

  // Check if this is a short modifier that should stay on same line
  const isShortModifier = SHORT_MODIFIERS.has(modifier || '')

  // Show both price and modifier
  // For short modifiers (+, -, +/-), keep on same line with whitespace-nowrap
  // For long modifiers (Motivated, Firm, Some Room), allow wrapping
  if (isShortModifier) {
    return (
      <span className={`${className} whitespace-nowrap`}>
        {formattedPrice} <span className="font-bold italic">{displayModifier}</span>
      </span>
    )
  }

  // For long modifiers, use flex wrap to allow modifier to go to new line when needed
  return (
    <span className={`${className} inline-flex flex-wrap justify-center gap-x-1`}>
      <span className="whitespace-nowrap">{formattedPrice}</span>
      <span className="font-bold italic">{displayModifier}</span>
    </span>
  )
}

export default PriceWithModifier

